import React, { FunctionComponent, useState, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import { Paper, makeStyles, Box } from '@material-ui/core';
import { BASE_URL } from '../../../../routes';
import Modal from '../../../../components/Modal';
import Loader from '../../../../components/Loader';
import DeleteDialog from '../../../../components/DeleteDialog';
import {
    InspectionList,
    Application,
    License,
    Appointment,
    Order,
    Inspection as InspectionType,
    ConfiguredFields,
    IColumn,
} from '../../../../store/modules/application/types';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';
import AddInspectionDialog from '../../../LicenseDetailTabs/Tabs/AddInspectionDialog';
import { getFlatColumnFieldsFromList, getFormattedColumns } from 'src/utils/utils';

type InspectionProps = {
    data: InspectionList;
    loading: boolean;
    addInspection: Function;
    deleteInspection: Function;
    application?: Application;
    users: [];
    hasMore: boolean;
    appointmentLicenses?: License[];
    appointment?: Appointment;
    skip: number;
    setSkip: Function;
    setSortOrder: Function;
    setSortColumn: Function;
    sortOrder: Order;
    sortBy: string;
    inspectionCountForAppointment: number;
    inspectionCountForLicense: number;
    inspectionColumnFields: ConfiguredFields;
};

const useStyles = makeStyles({
    noDataResultContainer: {
        width: '100%',
        maxHeight: 300,
        color: 'gray',
        paddingTop: '40px',
        paddingBottom: '150px',
    },
    addInspection: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
    },
    inspectionContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    tablePaginationInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px',
    },
    countSpan: {
        color: '#484848',
        fontSize: 14,
        letterSpacing: 0.1,
        display: 'flex',
        alignItems: 'center',
    },
});

const Inspection: FunctionComponent<InspectionProps> = (props) => {
    let columns: IColumn[];

    const {
        data,
        skip,
        sortBy,
        loading,
        setSkip,
        hasMore,
        sortOrder,
        appointment,
        application,
        setSortOrder,
        setSortColumn,
        addInspection,
        deleteInspection,
        appointmentLicenses,
        inspectionColumnFields,
        inspectionCountForLicense,
        inspectionCountForAppointment,
    } = props;
    let inspectionCount = inspectionCountForLicense || 0;
    columns = inspectionColumnFields?.fields?.length
        ? getFormattedColumns(inspectionColumnFields)
        : [
              {
                  id: 'inspectionType|string',
                  label: 'Inspection Type',
                  maxWidth: 50,
                  field: 'type.name',
                  sortFields: ['type.name'],
              },
              {
                  id: 'inspector|string',
                  label: 'Inspector',
                  align: 'left',
                  maxWidth: 300,
                  field: 'inspector.name',
                  sortFields: ['inspector.name'],
              },
              {
                  id: 'inspectionDate|date',
                  label: 'Inspection Date',
                  align: 'left',
                  maxWidth: 100,
                  field: 'inspectionDate',
                  sortFields: ['inspectionDate'],
              },
              {
                  id: 'inspectionStatus|string',
                  label: 'Inspection Status',
                  align: 'left',
                  maxWidth: 200,
                  field: 'status',
                  sortFields: ['status'],
              },
          ];

    if (appointment) {
        columns.unshift({ id: 'license|string', label: 'License', maxWidth: 50, field: 'license.licenseNumber' });
        inspectionCount = inspectionCountForAppointment;
    }
    const [inspectionId, setInspectionId] = useState<string>('');

    /* State for modals */

    const [isDeleteModalOpen, setModal] = useState(false);
    const [isAddInspectionModalOpen, setIsAddInspectionModalOpen] = useState(false);

    const classes = useStyles();
    const history = useHistory();

    const handleAddInspectionModal = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setIsAddInspectionModalOpen(true);
    };

    const handleAddInspection = (inspection: InspectionType) => {
        addInspection(inspection);
        setSkip(0);
    };

    /* Handle delete case button */
    const handleDeleteCase = (id: string, e: any) => {
        e.stopPropagation();
        setInspectionId(id);
        setModal(true);
    };
    const onClose = () => {
        setModal(false);
    };
    const onDelete = () => {
        setModal(false);
        deleteInspection(inspectionId);
    };

    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/inspection/${id}/detail`);
    };

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };

    const inspections = inspectionColumnFields?.fields?.length
        ? getFlatColumnFieldsFromList(data, columns)
        : data.map((inspection: any) => {
              return {
                  id: inspection?.id,
                  scheduledDate: inspection?.scheduledDate,
                  inspectionType: inspection?.type?.name || inspection?.type?.inspectionType,
                  inspector: inspection?.inspector?.name || '',
                  inspectionDate: inspection?.inspectionDate,
                  inspectionStatus: inspection?.status,
                  license: inspection?.license?.licenseNumber,
              };
          });
    if (loading) {
        return <Loader />;
    }

    return (
        <div className="verticalFillContainer">
            <div className={classes.inspectionContainer}>
                <Button
                    startIcon={<AddIcon />}
                    className={classes.addInspection}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                        handleAddInspectionModal && handleAddInspectionModal(e);
                    }}
                >
                    ADD INSPECTION
                </Button>
            </div>
            <Box className={classes.tablePaginationInfo}>
                <span className={classes.countSpan}>{`${inspectionCount} Results`}</span>
            </Box>
            <Paper className="case-entries-container verticalFillContent">
                {data.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={inspections}
                        count={inspections.length}
                        onClickRow={onClickRow}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        deleteFlag={true}
                        handleDeleteCase={handleDeleteCase}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                    />
                ) : (
                    <TableEmptyState
                        title="No active records for this license. "
                        message="All your inspections will be stored here."
                    ></TableEmptyState>
                )}

                {isDeleteModalOpen && (
                    <Modal open={true} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                        {isDeleteModalOpen && (
                            <DeleteDialog
                                onClose={onClose}
                                onDelete={onDelete}
                                contentTitle="Are you sure you'd like to proceed with this action?"
                                content=""
                            />
                        )}
                    </Modal>
                )}
            </Paper>
            {/* Modal for Add inspection */}
            <Modal open={isAddInspectionModalOpen} maxWidth={'sm'} fullWidth={true}>
                <AddInspectionDialog
                    appointment={appointment}
                    licenses={appointmentLicenses}
                    setIsAddInspectionModalOpen={setIsAddInspectionModalOpen}
                    addInspection={handleAddInspection}
                    application={application}
                />
            </Modal>
        </div>
    );
};

export default Inspection;
